import React, {useEffect, useState, useRef, useContext, useCallback} from 'react';
import {List} from 'immutable';
import masqueradeStore from 'generic/Masquerade/Masquerade.store';

import {
  addToast,
  Card,
  Button,
  Icon,
  Text,
  Anchor,
  LoadingSpinner,
  Heading
} from '@albert-io/atomic';

import QuestionActions from 'components/PracticeView/QuestionActions/QuestionActions.react';
import PaginatedResultsManager from 'components/PracticeView/helpers/paginatedResultsManager';
import {
  makeNavigateNextProps,
  makeNavigateBackProps
} from 'client/components/PracticeView/helpers/questionNavigation.utils';

import {hasMoreQuestionInfo} from 'components/PracticeView/practiceView.utils';

// TODO: Make types not related to VSS
import {
  QuestionSet,
  Question,
  Guess
} from 'client/Classrooms/Classroom/Teacher/Assignments/ViewStudentSubmission/ViewStudentSubmission.types';

import {OptionEliminatorProvider} from 'client/components/PracticeView/OptionEliminator/OptionEliminator.react';
import {EmptyQuestionShell} from 'client/components/PracticeView/EmptyQuestionShell/EmptyQuestionShell.react';
import QuestionRenderer from 'client/components/PracticeView/QuestionRenderer/QuestionRenderer.react';
import ContentDiscoveryReviewQuestionsList from 'components/ContentDiscovery/Document/ContentDiscoveryReviewQuestions/ContentDiscoveryReviewQuestionsList.react';

import classNames from 'classnames';

import {assignmentBuilderLink} from '../Document/DocumentContentDiscovery/AssignmentBuilderIntroModal/AssignmentBuilderIntroModal.react';
import {ContentDiscoveryContext} from '../ContentDiscoveryProvider.react';

import ContentDiscoveryAddQuestionsList from './ContentDiscoveryAddQuestionsList.react';

const QuestionPreviewSeparated = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showQuestionArea, setShowQuestionArea] = useState(false);
  const [activeQuestion, setActiveQuestion] = useState<Question>({} as Question);
  const [activeQuestionSet, setActiveQuestionSet] = useState<QuestionSet>({} as QuestionSet);
  const [activeGuess, setActiveGuess] = useState<Guess | null>(null);
  const [paginatedResultsManager, setPaginatedResultsManager] = useState<PaginatedResultsManager>(
    {} as PaginatedResultsManager
  );
  const [questionSets, setQuestionSets] = useState<List<any>>(List());

  const {
    questionsQuery,
    draftQuestionsQuery,
    currentPage,
    setCurrentPage,
    handleHashChange,
    hasFilters,
    addQuestionSetsToDraft,
    setSelectedQuestionSets,
    removeQuestionSet,
    isQuestionSetSelected,
    autoSave,
    selectedQuestionSets
  } = useContext(ContentDiscoveryContext);

  const questionsListQuery = currentPage === 'add' ? questionsQuery : draftQuestionsQuery;
  const mainWrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!autoSave) {
      setQuestionSets(selectedQuestionSets);
      if (currentPage === 'review' && selectedQuestionSets.size) {
        const currentQuestionSet = selectedQuestionSets.first() || null;
        const currentQuestion = currentQuestionSet?.getQuestions().first() || null;
        setActiveQuestionState(currentQuestion, currentQuestionSet);
      }
    }
  }, [selectedQuestionSets, autoSave, currentPage]);

  // Sets up PV and fetches question sets
  useEffect(() => {
    const executeQuery = async () => {
      if (!questionsListQuery || (!hasFilters && currentPage === 'add')) return;
      if (currentPage === 'review' && !autoSave) return; // don't make request for review page if not saved (autosave is false)
      try {
        setIsLoading(true);
        // @ts-ignore: todo: fix - safeguarded at L60 but TS isn't recognizing it
        await questionsListQuery.getResourcePromise();
      } catch (e) {
        addToast({
          message: 'something went wrong with the request.',
          title: 'Clear your filters!',
          type: 'danger'
        });
      }

      const currentPaginatedResultsManager = new PaginatedResultsManager({
        initialQuery: questionsListQuery,
        onUpdate: (incomingQuestionSets) => {
          if (incomingQuestionSets) {
            setQuestionSets((prevQuestionSets) => {
              const updatedQuestionSets = prevQuestionSets.concat(
                incomingQuestionSets
              ) as List<any>;
              const sortedUpdatedQuestionSets = updatedQuestionSets.sortBy((item) =>
                item!.getMeta().getAssignmentPosition()
              );
              return sortedUpdatedQuestionSets.toList();
            });
          }
        }
      });
      setPaginatedResultsManager(currentPaginatedResultsManager);

      const currentQuestionSet = currentPaginatedResultsManager.getResults().first() || null;
      const currentQuestion = currentQuestionSet?.getQuestions().first() || null;

      const currentQuestionSets = currentPaginatedResultsManager.getResults();

      setActiveQuestionState(currentQuestion, currentQuestionSet);
      setQuestionSets(currentQuestionSets);
      setIsLoading(false);
    };
    executeQuery();
  }, [questionsListQuery]);

  // handle switching pages when location changes (page nav buttons)
  useEffect(() => {
    window.addEventListener('hashchange', handleHashChange);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [handleHashChange]);

  const scrollToQuestionByIdFunc = useRef((id: string) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest'
    });
  });

  const resetGuess = () => {
    global.scrollTo(0, 0);
    setActiveGuess(null);
  };

  const setActiveGuessAndScroll = (guess) => {
    global.scrollTo(0, 0);
    setActiveGuess(guess);
  };

  const getFooterActions = () => {
    const actions: JSX.Element[] = [];
    let key = 0;

    if (hasMoreQuestionInfo()) {
      actions.push(
        <QuestionActions.MoreInfo
          key={(key += 1)}
          questionSetId={activeQuestionSet?.getId?.()}
          userId={masqueradeStore.getUserIdByMasqueradeState()}
        />
      );
    }
    actions.push(
      <QuestionActions.ToggleViewSolution
        key={(key += 1)}
        question={activeQuestion}
        isViewingSolution={!!activeGuess}
        handleToggleSolution={activeGuess ? resetGuess : setActiveGuessAndScroll}
      />
    );
    return (
      <QuestionActions questionStoreName={activeQuestion.getStore().getName()}>
        {actions}
      </QuestionActions>
    );
  };

  const addQuestionSet = useCallback(
    (e, questionSet) => {
      if (autoSave) {
        addQuestionSetsToDraft(List([questionSet]));
      } else {
        const newSelectedSets = List([...selectedQuestionSets.toArray(), questionSet]); // TODO: handle duplicates
        setSelectedQuestionSets(newSelectedSets);
      }
      e.stopPropagation();
    },
    [addQuestionSetsToDraft, setSelectedQuestionSets, selectedQuestionSets, autoSave]
  );

  const getCustomHeader = useCallback(() => {
    if (currentPage !== 'add') return undefined;
    if (!activeQuestionSet) return undefined;
    if (isQuestionSetSelected(activeQuestionSet)) {
      return (
        <div className='u-mar-lr_auto u-mar-tb_0'>
          <Text size='m' bold emphasis='low'>
            Added To Assignment
            <Icon icon='check' className='u-mar-l_1' />
          </Text>
          <Button
            size='s'
            variant='text'
            color='destructive'
            className='u-mar-l_1'
            onClick={(e) => removeQuestionSet(e, activeQuestionSet)}
          >
            Remove
          </Button>
        </div>
      );
    }
    return (
      <Button
        data-test-id='AB-sidebar-preview__add-question-btn'
        size='s'
        className='u-mar-lr_auto'
        onClick={(e) => addQuestionSet(e, activeQuestionSet)}
      >
        Add To Assignment
      </Button>
    );
  }, [activeQuestionSet, currentPage, isQuestionSetSelected, removeQuestionSet, addQuestionSet]);

  const updateScrollPosition = useCallback(() => {
    mainWrapperRef?.current?.scrollTo(0, 0);
  }, [mainWrapperRef]);

  // Sets new active question
  const setActiveQuestionState = (question, questionSet) => {
    setActiveQuestion(question);
    setActiveQuestionSet(questionSet);
    // active question area is safe to show
    setShowQuestionArea(true);
    // build active question guess
    const builtGuess = question?.getStore().buildCorrectGuess(question);
    setActiveGuess(builtGuess);
    updateScrollPosition();
  };

  // Get total of questions in response
  const totalQuestions = questionsQuery
    ?.getResourceMetadata()
    .getIn(['page', 'total_entries'], null);

  // Loading
  if (isLoading) {
    return (
      <div className='u-width_100pc u-display_flex u-justify-content_center u-align-items_center'>
        <LoadingSpinner size={4} />
      </div>
    );
  }

  if (!hasFilters && currentPage === 'add') {
    // sidebar overlay now displays no filters messaging see "components/ContentDiscovery/Document/DocumentContentDiscovery//FilterGroup/FilterGroup.react"
    return null;
  }

  if (
    !questionSets.size &&
    !paginatedResultsManager.isRequestInFlight &&
    hasFilters &&
    currentPage === 'add'
  ) {
    const questionSets = paginatedResultsManager?.getResults?.();
    if (!questionSets || questionSets.size === 0) {
      // sidebar overlay now displays no results messaging see "components/ContentDiscovery/Document/DocumentContentDiscovery//FilterGroup/FilterGroup.react"
      return null;
    }
  }

  const hasUnsavedQuestions = questionSets.size > 0 && !autoSave;

  // When removing last question from review page, active question becomes null before questionSet size updates
  // TODO investigate if the below is true
  if (
    (!questionsListQuery || !questionSets.size || !activeQuestion) &&
    currentPage === 'review' &&
    !hasUnsavedQuestions
  ) {
    return (
      <div className='cd-question-list_empty-container u-mar-lr_auto u-mar-t_4 u-display_flex u-flex-direction_column'>
        <Card className='cd-question-list_empty-container__card u-height_fit-content '>
          <Heading>Welcome to the Assignment Builder!</Heading>
          <Text size='m' className='u-mar-b_3 u-mar-t_1'>
            Get started by adding questions to your assignment.
          </Text>
          <Button
            data-testid='assignment-builder__add-questions-btn'
            className='u-width_fit-content u-height_fit-content'
            onClick={() => setCurrentPage('add')}
          >
            Add Questions <Icon className='u-pad-l_1' icon='plus' />
          </Button>
        </Card>
        <Card className='u-mar-t_4 cd-question-list_empty-container__card cd-question-list_empty-container__card--video'>
          <Text>Watch our announcement video below:</Text>
          <iframe allowFullScreen title='Assignment Builder Intro' src={assignmentBuilderLink} />
          <div className='u-display_flex u-flex-direction_column'>
            <Text className='u-mar-b_0' as='p'>
              Need more guidance?
            </Text>
            <Text className='u-mar-b_0' as='p'>
              Read {` `}
              <Anchor target='_blank' href='https://help.albert.io/en/articles/8725620'>
                our step-by-step guide
              </Anchor>
            </Text>
          </div>
        </Card>
      </div>
    );
  }

  if (
    // TODO: work on this condition, (shows up when going from unsaved to autosave)
    !(hasUnsavedQuestions && currentPage === 'review') &&
    (Object.keys(paginatedResultsManager).length === 0 || !paginatedResultsManager.getResults())
  ) {
    return (
      <div>
        The paginated results manager does not have any results. Request in progress ={' '}
        {Object.keys(paginatedResultsManager).length === 0
          ? 'no manager yet'
          : paginatedResultsManager?.isRequestInFlight}
      </div>
    );
  }

  return (
    <>
      {currentPage === 'add' ? (
        <ContentDiscoveryAddQuestionsList
          scrollToQuestionByIdFunc={scrollToQuestionByIdFunc}
          setActiveQuestionState={setActiveQuestionState}
          activeQuestion={activeQuestion}
          paginatedResultsManager={paginatedResultsManager}
          totalQuestions={totalQuestions}
        />
      ) : (
        <ContentDiscoveryReviewQuestionsList
          scrollToQuestionByIdFunc={scrollToQuestionByIdFunc}
          questionSets={questionSets}
          setActiveQuestionState={setActiveQuestionState}
          activeQuestion={activeQuestion}
          paginatedResultsManager={paginatedResultsManager}
          showQuestionArea={showQuestionArea}
        />
      )}
      {showQuestionArea ? (
        <OptionEliminatorProvider>
          {activeQuestion ? (
            <div
              className={classNames('u-overflow_auto', {
                'cd-question-list__column': currentPage === 'review',
                'cd-question-list__column-square': currentPage === 'add'
              })}
              ref={mainWrapperRef}
            >
              <QuestionRenderer
                activeQuestion={activeQuestion}
                activeQuestionSet={activeQuestionSet}
                activeSubject={activeQuestionSet?.getSubject?.()}
                actions={getFooterActions()}
                customHeader={getCustomHeader()}
                headingActions={null}
                interactive
                rendererType={activeGuess ? 'PostGuess' : 'PreGuess'}
                navigateBackProps={
                  autoSave && // TODO: investigate if navigagte props works withoiut paginated results query
                  makeNavigateBackProps({
                    paginatedQuestionsListManager: paginatedResultsManager,
                    activeQuestionSet,
                    activeQuestion,
                    setActiveQuestionState,
                    scrollToQuestionByIdFunc
                  })
                }
                navigateNextProps={
                  autoSave && // TODO: investigate if navigagte props works withoiut paginated results query
                  makeNavigateNextProps({
                    paginatedQuestionsListManager: paginatedResultsManager,
                    activeQuestionSet,
                    activeQuestion,
                    setActiveQuestionState,
                    scrollToQuestionByIdFunc
                  })
                }
                activeQuestionStore={activeQuestion && activeQuestion.getStore?.()}
                activeGuess={activeGuess}
                showDifficulty
                postSubmissionScrollPointRef={() => {}}
                hasQuestionSetAccessPredicate={(s) => !!s}
              />
            </div>
          ) : (
            <EmptyQuestionShell />
          )}
        </OptionEliminatorProvider>
      ) : null}
    </>
  );
};

export default QuestionPreviewSeparated;
